// src/components/WalletView.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getAddress } from "ethers";
import { Tooltip, List, Spin, Input, Segmented } from "antd";
import axios from "axios";
import { CHAINS_CONFIG } from "../chains";
import { ethers } from "ethers";
import { useNavigate, useLocation } from "react-router-dom";
import SwapInWallet from "./SwapInWallet";
import ImportToken from "./ImportToken";
import { getTokensByChain, saveToken } from "../utils/tokenStorage";
import ERC20ABI from "../abi/ERC20.json";
import TokenDetails from "./TokenDetails";
import BridgeWidget from "./BridgeWidget";
import { getTokenLogoUrl, getFallbackLogo } from "../utils/tokenLogoUtils";
import WalletBackground from "./WalletBackground";
import Dashboard from "../pages/Dashboard";
import "./Walletview.css";
import "./WalletActivity.css";
import BaitLogo from "../BaitLogo.png";
import {
  CopyOutlined,
  ReloadOutlined,
  SwapOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ApiOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import {
  ArrowUpRight,
  ArrowDownLeft,
  ExternalLink,
  Copy,
  Check,
  ArrowLeft,
} from "lucide-react";

// WUSDT constant address
const WUSDT_ADDRESS = "0x4791394e566020D7a69A6029D4FD1324B9706107";
const USDT_ADDRESS = "0xc2132D05D31c914a87C6611C10748AEb04B58e8F";

// Tether logo URL (stable, verified)
const TETHER_LOGO_URL =
  "https://cryptologos.cc/logos/tether-usdt-logo.png";

// Same activity API used by Activity.js (GoldChain)
const API_URL = "https://baitcoinexplorer.bait-coin.com";

// Backend proxy (used for Polygon transactions)
const BACKEND_API_URL = "https://backend.bait-coin.com";

// Key used to remember tokens the user removed on purpose
const REMOVED_TOKENS_KEY = "baitpay:removedTokens";


// Chain ID mapping for DENO logo service
const CHAIN_IDS = {
  ethereum: "1",
  polygon: "137",
  bsc: "56",
  arbitrum: "42161",
  avalanche: "43114",
  optimism: "10",
  base: "8453",
  gnosis: "100",
  fantom: "250",
  celo: "42220",
  moonbeam: "1284",
  moonriver: "1285",
  cronos: "25",
  harmony: "1666600000",
  fuse: "122",
  aurora: "1313161554",
  metis: "1088",
  boba: "288",
  polygon_zkevm: "1101",
  zksync: "324",
  linea: "59144",
  scroll: "534352",
  mantle: "5000",
  pulsechain: "369",
  goldchain: "777771",
};

// ============================================================
// CHAIN KEY NORMALIZATION
// ============================================================
const chainStorageKey = (chain) => {
  if (!chain) return "";
  const key = String(chain).toLowerCase().trim();
  if (
    key === "0x777771" ||
    key === "777771" ||
    key === "0xbdd5b" ||
    key === "goldchain"
  ) {
    return "goldchain";
  }
  if (key === "0x89" || key === "polygon" || key === "matic") return "polygon";
  if (key === "0x1" || key === "ethereum") return "ethereum";
  if (key === "0x13881" || key === "mumbai" || key === "mumbai testnet")
    return "mumbai";
  if (key === "0x38" || key === "0x56" || key === "bsc") return "bsc";
  if (key === "0xa4b1" || key === "arbitrum") return "arbitrum";
  if (key === "0xa" || key === "optimism") return "optimism";
  if (key === "0x2105" || key === "base") return "base";
  if (key === "0xa86a" || key === "avalanche") return "avalanche";
  return key;
};

// ============================================================
// ACTIVITY CHAIN HELPERS
// ============================================================
const isPolygonChain = (chain) => {
  if (!chain) return false;
  const s = String(chain).toLowerCase().trim();
  return (
    s === "0x89" ||
    s === "137" ||
    s.includes("polygon") ||
    s.includes("matic") ||
    s === "pol"
  );
};

const getExplorerTxUrl = (chain, hash) => {
  if (!hash) return "#";
  if (isPolygonChain(chain)) {
    return `https://polygonscan.com/tx/${hash}`;
  }
  return `${API_URL}/#/transaction/${hash}`;
};

const getExplorerLabel = (chain) => {
  if (isPolygonChain(chain)) return "PolygonScan";
  return "Explorer";
};

// ============================================================
// NATIVE TOKEN HELPERS
// ============================================================
const resolveNativeSymbol = (chain) => {
  if (!chain) return "BAIT";
  const key = String(chain).toLowerCase().trim();
  if (key === "0x777771" || key === "goldchain") return "BAIT";
  if (key === "0x89" || key === "polygon" || key === "matic") return "POL";
  if (key === "0x1" || key === "ethereum") return "ETH";
  if (key === "0x13881" || key === "mumbai") return "MATIC";
  if (key === "0x38" || key === "0x56" || key === "bsc") return "BNB";
  if (key === "0xa4b1" || key === "arbitrum") return "ETH";
  if (key === "0xa" || key === "optimism") return "ETH";
  if (key === "0x2105" || key === "base") return "ETH";
  if (key === "0xa86a" || key === "avalanche") return "AVAX";
  try {
    const cfg = CHAINS_CONFIG[chain];
    if (cfg && cfg.ticker) return cfg.ticker;
  } catch (e) {
    // ignore
  }
  return "BAIT";
};

const resolveNativeName = (chain) => {
  const sym = resolveNativeSymbol(chain);
  if (sym === "BAIT") return "Gold Backed Token";
  if (sym === "POL") return "Polygon";
  if (sym === "MATIC") return "Polygon (Mumbai)";
  if (sym === "ETH") return "Ethereum";
  if (sym === "BNB") return "BNB Chain";
  if (sym === "AVAX") return "Avalanche";
  return "Native Token";
};

const resolveNativeLogo = (chain) => {
  const sym = resolveNativeSymbol(chain);
  if (sym === "POL" || sym === "MATIC") {
    return "https://cryptologos.cc/logos/polygon-matic-logo.png";
  }
  if (sym === "ETH") {
    return "https://cryptologos.cc/logos/ethereum-eth-logo.png";
  }
  if (sym === "BNB") {
    return "https://cryptologos.cc/logos/bnb-bnb-logo.png";
  }
  if (sym === "AVAX") {
    return "https://cryptologos.cc/logos/avalanche-avax-logo.png";
  }
  return BaitLogo;
};

const isGoldChainSelected = (chain) => {
  if (!chain) return false;
  const key = String(chain).toLowerCase().trim();
  return (
    key === "0x777771" ||
    key === "goldchain" ||
    key === "777771" ||
    key === "0xbdd5b"
  );
};

const readRemovedTokens = () => {
  try {
    const raw = localStorage.getItem(REMOVED_TOKENS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.map((a) => String(a).toLowerCase())
      : [];
  } catch {
    return [];
  }
};

const writeRemovedTokens = (list) => {
  try {
    localStorage.setItem(REMOVED_TOKENS_KEY, JSON.stringify(list));
  } catch (err) {
    console.error("Failed to persist removed tokens:", err);
  }
};

const removeTokenFromLocalStorage = (address) => {
  const target = String(address).toLowerCase();
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || key === REMOVED_TOKENS_KEY) continue;
      const raw = localStorage.getItem(key);
      if (!raw || !raw.trim().startsWith("[")) continue;

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        continue;
      }
      if (!Array.isArray(parsed)) continue;

      const matches = (entry) =>
        entry &&
        typeof entry === "object" &&
        String(entry.address || "").toLowerCase() === target;

      if (!parsed.some(matches)) continue;
      localStorage.setItem(
        key,
        JSON.stringify(parsed.filter((e) => !matches(e)))
      );
    }
  } catch (err) {
    console.error("Failed to remove token from storage:", err);
  }
};

// Normalizers
const normSym = (s) => String(s || "").toUpperCase().trim();
const normAddr = (a) => String(a || "").toLowerCase().trim();

// ============================================================
// AMOUNT FORMATTER
// ============================================================
const formatAmount = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  const fixed = n.toFixed(6);
  const trimmed = fixed.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed || "0";
};

// ============================================================
// STABLECOIN LOGO RESOLVER
// ============================================================
const isStablecoinSymbol = (sym) => {
  const s = normSym(sym);
  return s === "USDT" || s === "USDT0" || s === "WUSDT";
};

// ============================================================
// POLYGON TRANSACTION NORMALIZER
// ============================================================
const normalizePolygonTx = (tx, walletAddress) => {
  const addr = (walletAddress || "").toLowerCase();
  const from = tx.from || "";
  const to = tx.to || "";
  const fromL = from.toLowerCase();
  const toL = to.toLowerCase();

  let txType = "Sent";
  if (fromL !== addr && toL === addr) txType = "Received";

  const gasUsed = Number(tx.gasUsed || 0);
  const gasPrice = Number(tx.gasPrice || 0);
  const value = Number(tx.value || 0) / 1e18;

  const success =
    String(tx.isError) === "0" &&
    (tx.txreceipt_status === undefined ||
      String(tx.txreceipt_status) === "1");

  return {
    hash: tx.hash,
    from,
    to,
    blockNumber: tx.blockNumber,
    confirmations: tx.confirmations,
    status: success ? 1 : 0,
    txType,
    amount: value,
    valueBAIT: value, // legacy field used by existing render code
    gasUsed,
    gasPrice,
    effectiveGasPrice: gasPrice,
    formattedTime: tx.timeStamp
      ? new Date(Number(tx.timeStamp) * 1000).toISOString()
      : null,
  };
};

// ============================================================
// POLYGON FETCHER (via backend proxy)
// ============================================================
const fetchPolygonTransactions = async (address) => {
  if (!address) return [];
  const { data } = await axios.get(
    `${BACKEND_API_URL}/api/polygon/transactions`,
    {
      params: { address },
      timeout: 15000,
    }
  );
  if (!data?.success) {
    throw new Error(data?.message || "Failed to fetch Polygon transactions.");
  }
  if (!Array.isArray(data.transactions)) return [];
  return data.transactions
    .map((tx) => normalizePolygonTx(tx, address))
    .filter((tx) => tx.amount > 0);
};

function WalletView({
  wallet,
  seedPhrase,
  selectedChain,
  setSelectedChain,
  logout,
  walletPage = "main",
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const nativeSymbol = resolveNativeSymbol(selectedChain);
  const nativeName = resolveNativeName(selectedChain);
  const nativeLogo = resolveNativeLogo(selectedChain);
  const isGold = isGoldChainSelected(selectedChain);
  const storageKey = chainStorageKey(selectedChain);

  const [balance, setBalance] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [amountToSend, setAmountToSend] = useState("");
  const [sendToAddress, setSendToAddress] = useState("");
  const [processing, setProcessing] = useState(false);
  const [hash, setHash] = useState(null);
  const [baitPrice, setBaitPrice] = useState(null);
  const [importedTokens, setImportedTokens] = useState([]);
  const [tokenBalances, setTokenBalances] = useState({});
  const [tokenLogos, setTokenLogos] = useState({});
  const [selectedToken, setSelectedToken] = useState(null);
  const [activeTab, setActiveTab] = useState("tokens");
  const [searchQuery, setSearchQuery] = useState("");
  const [copied, setCopied] = useState(false);

  // Activity tab state
  const [transactions, setTransactions] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activitySearchText, setActivitySearchText] = useState("");
  const [activityFilterType, setActivityFilterType] = useState("all");
  const [activityPage, setActivityPage] = useState(1);
  const [expandedTxHash, setExpandedTxHash] = useState(null);
  const [copiedActivityValue, setCopiedActivityValue] = useState("");

  // Asset selection
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(() => ({
    symbol: resolveNativeSymbol(selectedChain),
    name: resolveNativeName(selectedChain),
    logo: resolveNativeLogo(selectedChain),
    address: null,
    decimals: 18,
    isNative: true,
  }));

  // Tokens the user explicitly removed
  const [removedTokens, setRemovedTokens] = useState(readRemovedTokens);

  // BAITPAY / BAITCASH toggle
  const [appMode, setAppMode] = useState(
    location.pathname === "/dashboard" ? "BAITCASH" : "BAITPAY"
  );

  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setAppMode("BAITCASH");
    } else if (
      location.pathname.startsWith("/yourwallet") ||
      location.pathname.startsWith("/swap") ||
      location.pathname.startsWith("/bridge") ||
      location.pathname.startsWith("/send")
    ) {
      setAppMode("BAITPAY");
    }
  }, [location]);

  // Reset selected asset when chain changes
  useEffect(() => {
    setSelectedAsset({
      symbol: resolveNativeSymbol(selectedChain),
      name: resolveNativeName(selectedChain),
      logo: resolveNativeLogo(selectedChain),
      address: null,
      decimals: 18,
      isNative: true,
    });
    setShowAssetDropdown(false);
  }, [selectedChain]);

  // Reset activity state when chain changes
  useEffect(() => {
    setTransactions([]);
    setExpandedTxHash(null);
    setActivityPage(1);
    setActivityError("");
  }, [selectedChain]);

  // Asset options
  const assetOptions = useMemo(() => {
    const options = [
      {
        symbol: nativeSymbol,
        name: nativeName,
        logo: nativeLogo,
        address: null,
        decimals: 18,
        isNative: true,
      },
    ];

    if (isGold) {
      options.push({
        symbol: "WUSDT",
        name: "Wrapped USDT",
        logo: TETHER_LOGO_URL,
        address: WUSDT_ADDRESS,
        decimals: 6,
        isNative: false,
      });
    }

    if (storageKey === "ethereum" || storageKey === "polygon") {
      options.push({
        symbol: "USDT",
        name: "Tether USD",
        logo: TETHER_LOGO_URL,
        address: USDT_ADDRESS,
        decimals: 6,
        isNative: false,
      });
    }

    return options;
  }, [selectedChain, nativeSymbol, nativeName, nativeLogo, isGold, storageKey]);

  // Helpers
  const formatAddress = (addr) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyAddress = async () => {
    if (!wallet) return;
    try {
      await navigator.clipboard.writeText(wallet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Failed to copy wallet address:", error);
      setCopied(false);
    }
  };

  const getDenoLogoUrl = useCallback((address, chain) => {
    if (!address || !chain) return null;
    try {
      const checksum = getAddress(address);
      const k = chainStorageKey(chain);
      const chainId = CHAIN_IDS[k];
      if (chainId) {
        return `https://token-logos.deno.dev/token/${chainId}/${checksum}`;
      }
      return null;
    } catch (e) {
      console.log(`Error creating DENO logo URL for ${address}:`, e);
      return null;
    }
  }, []);

  const fetchAllTokenLogos = useCallback(async () => {
    if (!importedTokens.length || !selectedChain) return;
    const logos = {};
    for (const token of importedTokens) {
      try {
        if (isStablecoinSymbol(token.symbol)) {
          logos[token.address] = TETHER_LOGO_URL;
          continue;
        }
        const logoUrl = getDenoLogoUrl(token.address, selectedChain);
        if (logoUrl) logos[token.address] = logoUrl;
      } catch (error) {
        console.log(`Failed to create logo URL for ${token.symbol}:`, error);
      }
    }
    setTokenLogos(logos);
  }, [importedTokens, selectedChain, getDenoLogoUrl]);

  useEffect(() => {
    fetchAllTokenLogos();
  }, [fetchAllTokenLogos]);

  async function fetchBaitPrice() {
    try {
      const res = await axios.get(
        "https://goldapi.bait-coin.com/api/gold-reference"
      );
      setBaitPrice(res.data?.bait?.price_usd ?? 0);
    } catch (err) {
      console.log("BAIT price error", err);
    }
  }

  useEffect(() => {
    fetchBaitPrice();
    const i = setInterval(fetchBaitPrice, 60000);
    return () => clearInterval(i);
  }, []);

  // ==================== ACTIVITY ====================
  const fetchTransactions = useCallback(async () => {
    try {
      const address = wallet || localStorage.getItem("address");
      if (!address) {
        setTransactions([]);
        setActivityLoading(false);
        return;
      }
      setActivityLoading(true);
      setActivityError("");

      // ✅ Route based on the active chain
      if (isPolygonChain(selectedChain)) {
        try {
          const list = await fetchPolygonTransactions(address);
          setTransactions(list);
        } catch (err) {
          console.error("Polygon Activity Error:", err);
          setTransactions([]);
          setActivityError(
            err?.message || "Unable to load Polygon transactions."
          );
        }
      } else {
        const response = await axios.get(
          `${API_URL}/api/address/${address}/transactions`
        );
        if (
          response.data?.success &&
          Array.isArray(response.data.transactions)
        ) {
          setTransactions(response.data.transactions);
        } else {
          setTransactions([]);
        }
      }
    } catch (err) {
      console.error("Wallet Activity Error:", err);
      setTransactions([]);
      setActivityError(err?.message || "Unable to load activity.");
    } finally {
      setActivityLoading(false);
    }
  }, [wallet, selectedChain]);

  useEffect(() => {
    if (activeTab !== "activity") return;
    fetchTransactions();
    const interval = setInterval(() => {
      fetchTransactions();
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTab, fetchTransactions]);

  const shortActivityAddress = (address) => {
    if (!address) return "-";
    return `${address.substring(0, 6)}...${address.substring(
      address.length - 4
    )}`;
  };

  const formatActivityDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const copyActivityValue = (value, type) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedActivityValue(type);
    setTimeout(() => setCopiedActivityValue(""), 2000);
  };

  const filteredActivityTransactions = useMemo(() => {
    const search = activitySearchText.toLowerCase().trim();
    return transactions.filter((tx) => {
      const searchable = `${tx.from || ""} ${tx.to || ""} ${
        tx.hash || ""
      }`.toLowerCase();
      if (activityFilterType === "received" && tx.txType !== "Received") {
        return false;
      }
      if (activityFilterType === "sent" && tx.txType === "Received") {
        return false;
      }
      if (search && !searchable.includes(search)) {
        return false;
      }
      return true;
    });
  }, [transactions, activitySearchText, activityFilterType]);

  const activityPageSize = 8;
  const paginatedActivityTransactions = useMemo(() => {
    return filteredActivityTransactions.slice(
      (activityPage - 1) * activityPageSize,
      activityPage * activityPageSize
    );
  }, [filteredActivityTransactions, activityPage]);

  const activityTotalPages = Math.ceil(
    filteredActivityTransactions.length / activityPageSize
  );

  useEffect(() => {
    setActivityPage(1);
  }, [activitySearchText, activityFilterType]);

  // ==================== ACTIVITY TAB RENDERER ====================
  const renderActivityTab = () => {
    const explorerLabel = getExplorerLabel(selectedChain);

    return (
      <div className="wallet-activity-tab">
        <div className="wallet-activity-toolbar">
          <div>
            <h3>Recent Transactions</h3>
            <p>
              {isPolygonChain(selectedChain)
                ? "Polygon (POL) activity"
                : "GoldChain activity"}
            </p>
          </div>
          <div className="wallet-activity-tools">
            <Input
              placeholder="Search address or hash"
              allowClear
              prefix={<SearchOutlined />}
              value={activitySearchText}
              onChange={(e) => setActivitySearchText(e.target.value)}
              className="wallet-activity-search"
            />
            <select
              value={activityFilterType}
              onChange={(e) => setActivityFilterType(e.target.value)}
              className="wallet-activity-filter"
            >
              <option value="all">All</option>
              <option value="received">Received</option>
              <option value="sent">Sent</option>
            </select>
            <button
              type="button"
              className="wallet-activity-refresh"
              onClick={fetchTransactions}
              title="Refresh transactions"
            >
              <ReloadOutlined />
            </button>
          </div>
        </div>

        {activityLoading && transactions.length === 0 && (
          <div className="wallet-activity-state">
            <Spin />
            <span>Loading transactions...</span>
          </div>
        )}

        {!activityLoading && activityError && (
          <div className="wallet-activity-state wallet-activity-empty">
            <div className="wallet-activity-empty-icon">⚠️</div>
            <strong>Unable to load activity</strong>
            <span>{activityError}</span>
            {isPolygonChain(selectedChain) && wallet && (
              <a
                href={`https://polygonscan.com/address/${wallet}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#8247e5",
                  color: "#fff",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: "bold",
                  marginTop: "16px",
                }}
              >
                <ExternalLink size={14} />
                View on PolygonScan
              </a>
            )}
          </div>
        )}

        {!activityLoading && !activityError && transactions.length === 0 && (
          <div className="wallet-activity-state wallet-activity-empty">
            <div className="wallet-activity-empty-icon">📭</div>
            <strong>No Activity Yet</strong>
            <span>
              Your wallet has no {isPolygonChain(selectedChain)
                ? "Polygon"
                : "GoldChain"}{" "}
              transactions.
            </span>
          </div>
        )}

        {transactions.length > 0 &&
          filteredActivityTransactions.length === 0 && (
            <div className="wallet-activity-state wallet-activity-empty">
              <div className="wallet-activity-empty-icon">🔎</div>
              <strong>No Matching Transactions</strong>
              <span>Try another address, hash, or filter.</span>
            </div>
          )}

        {filteredActivityTransactions.length > 0 && (
          <>
            <div className="wallet-activity-list">
              {paginatedActivityTransactions.map((tx, index) => {
                const isExpanded = expandedTxHash === tx.hash;
                return (
                  <div
                    key={tx.hash || index}
                    className="wallet-activity-item-wrapper"
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      className={`wallet-activity-card ${
                        isExpanded ? "expanded" : ""
                      }`}
                      onClick={() =>
                        setExpandedTxHash(isExpanded ? null : tx.hash)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setExpandedTxHash(isExpanded ? null : tx.hash);
                        }
                      }}
                    >
                      <div
                        className={`wallet-activity-icon ${
                          tx.txType === "Received" ? "received" : "sent"
                        }`}
                      >
                        {tx.txType === "Received" ? (
                          <ArrowDownLeft size={18} />
                        ) : (
                          <ArrowUpRight size={18} />
                        )}
                      </div>
                      <div className="wallet-activity-main">
                        <div className="wallet-activity-title">
                          {tx.txType === "Received" ? "Received" : "Sent"}{" "}
                          {nativeSymbol}
                        </div>
                        <div className="wallet-activity-address">
                          {tx.txType === "Received"
                            ? shortActivityAddress(tx.from)
                            : shortActivityAddress(tx.to)}
                        </div>
                        <div className="wallet-activity-date">
                          {formatActivityDate(tx.formattedTime)}
                        </div>
                      </div>
                      <div className="wallet-activity-amount-wrap">
                        <div
                          className={`wallet-activity-amount ${
                            tx.txType === "Received" ? "received" : "sent"
                          }`}
                        >
                          {tx.txType === "Received" ? "+" : "-"}
                          {formatAmount(tx.amount ?? tx.valueBAIT)}{" "}
                          {nativeSymbol}
                        </div>
                        <div
                          className={`wallet-activity-status ${
                            tx.status === 1 ? "confirmed" : "failed"
                          }`}
                        >
                          {tx.status === 1 ? "Confirmed" : "Failed"}
                        </div>
                      </div>
                      <div
                        className={`wallet-activity-arrow ${
                          isExpanded ? "open" : ""
                        }`}
                      >
                        ›
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="wallet-activity-dropdown">
                        <div className="wallet-activity-detail-list">
                          <div className="wallet-activity-detail-row">
                            <span>Status</span>
                            <strong>
                              {tx.status === 1 ? "Confirmed" : "Failed"}
                            </strong>
                          </div>
                          <div className="wallet-activity-detail-row">
                            <span>Date</span>
                            <strong>
                              {formatActivityDate(tx.formattedTime) || "-"}
                            </strong>
                          </div>
                          {[
                            ["From", tx.from, "from"],
                            ["To", tx.to, "to"],
                            ["Transaction Hash", tx.hash, "hash"],
                          ].map(([label, value, type]) => (
                            <div
                              className="wallet-activity-detail-row"
                              key={type}
                            >
                              <span>{label}</span>
                              <div className="wallet-activity-copy-row">
                                <code>{value || "-"}</code>
                                {value && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyActivityValue(value, type);
                                    }}
                                    title={`Copy ${label}`}
                                  >
                                    {copiedActivityValue === type ? (
                                      <Check size={14} />
                                    ) : (
                                      <Copy size={14} />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          <div className="wallet-activity-detail-row">
                            <span>Block</span>
                            <strong>{tx.blockNumber || "-"}</strong>
                          </div>
                          <div className="wallet-activity-detail-row">
                            <span>Confirmations</span>
                            <strong>{tx.confirmations || "0"}</strong>
                          </div>
                          <div className="wallet-activity-detail-row">
                            <span>Total Amount</span>
                            <strong>
                              {formatAmount(tx.amount ?? tx.valueBAIT)}{" "}
                              {nativeSymbol}
                            </strong>
                          </div>
                        </div>
                        {tx.hash && (
                          <a
                            className="wallet-activity-explorer-link"
                            href={getExplorerTxUrl(selectedChain, tx.hash)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink size={14} />
                            View on {explorerLabel}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {activityTotalPages > 1 && (
              <div className="wallet-activity-pagination">
                <button
                  type="button"
                  disabled={activityPage === 1}
                  onClick={() =>
                    setActivityPage((page) => Math.max(1, page - 1))
                  }
                >
                  Previous
                </button>
                <span>
                  Page {activityPage} of {activityTotalPages}
                </span>
                <button
                  type="button"
                  disabled={activityPage === activityTotalPages}
                  onClick={() =>
                    setActivityPage((page) =>
                      Math.min(activityTotalPages, page + 1)
                    )
                  }
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ============================================================
  // LOAD IMPORTED TOKENS
  // ============================================================
  const loadImportedTokens = useCallback(() => {
    const primaryKey = chainStorageKey(selectedChain);
    const rawKey = String(selectedChain || "").toLowerCase();

    const listA = getTokensByChain(primaryKey) || [];
    const listB =
      rawKey && rawKey !== primaryKey ? getTokensByChain(rawKey) || [] : [];

    const merged = [];
    const seenAddr = new Set();
    const seenSym = new Set();

    const pushUnique = (t) => {
      if (!t || typeof t !== "object") return;
      const addr = normAddr(t.address);
      const sym = normSym(t.symbol);

      if (addr && seenAddr.has(addr)) return;
      if (!addr && sym && seenSym.has(sym)) return;

      if (addr) seenAddr.add(addr);
      if (sym) seenSym.add(sym);
      merged.push(t);
    };

    listA.forEach(pushUnique);
    listB.forEach(pushUnique);

    const removedList = readRemovedTokens();
    const filtered = merged.filter(
      (t) => !removedList.includes(normAddr(t.address))
    );

    setImportedTokens(filtered);
    setRemovedTokens(removedList);
  }, [selectedChain]);

  const handleUserImported = useCallback(() => {
    const primaryKey = chainStorageKey(selectedChain);
    const rawKey = String(selectedChain || "").toLowerCase();

    const listA = getTokensByChain(primaryKey) || [];
    const listB =
      rawKey && rawKey !== primaryKey ? getTokensByChain(rawKey) || [] : [];

    const allAddresses = new Set();
    [...listA, ...listB].forEach((t) => {
      const addr = normAddr(t?.address);
      if (addr) allAddresses.add(addr);
    });

    const currentRemoved = readRemovedTokens();
    const stillRemoved = currentRemoved.filter(
      (addr) => !allAddresses.has(addr)
    );

    if (stillRemoved.length !== currentRemoved.length) {
      writeRemovedTokens(stillRemoved);
      setRemovedTokens(stillRemoved);
    }

    loadImportedTokens();
  }, [selectedChain, loadImportedTokens]);

  const handleDeleteToken = useCallback(
    (token) => {
      const address = token?.address;
      if (!address || token.isBait || token.isNative) return;

      const lower = normAddr(address);

      removeTokenFromLocalStorage(address);

      const currentRemoved = readRemovedTokens();
      const next = currentRemoved.includes(lower)
        ? currentRemoved
        : [...currentRemoved, lower];
      writeRemovedTokens(next);
      setRemovedTokens(next);

      setImportedTokens((prev) =>
        prev.filter((t) => normAddr(t.address) !== lower)
      );
      setTokenBalances((prev) => {
        const updated = { ...prev };
        delete updated[address];
        delete updated[lower];
        return updated;
      });
      setTokenLogos((prev) => {
        const updated = { ...prev };
        delete updated[address];
        delete updated[lower];
        return updated;
      });
      setSelectedToken((prev) =>
        prev && normAddr(prev.address) === lower ? null : prev
      );
      setSelectedAsset((prev) =>
        prev && normAddr(prev.address) === lower ? assetOptions[0] : prev
      );
    },
    [assetOptions]
  );

  useEffect(() => {
    loadImportedTokens();
  }, [loadImportedTokens]);

  // Fetch ERC20 balances
  const fetchTokenBalances = useCallback(async () => {
    if (!wallet) return;
    try {
      const chain = CHAINS_CONFIG[selectedChain];
      if (!chain || !chain.rpcUrl) return;
      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
      const balances = {};
      if (isGold) {
        try {
          const contract = new ethers.Contract(
            WUSDT_ADDRESS,
            ERC20ABI,
            provider
          );
          const raw = await contract.balanceOf(wallet);
          const wusdtBalance = Number(ethers.formatUnits(raw, 6));
          balances[WUSDT_ADDRESS] = wusdtBalance;
        } catch (err) {
          console.error("Error fetching WUSDT balance:", err);
          balances[WUSDT_ADDRESS] = 0;
        }
      }
      for (const token of importedTokens) {
        try {
          let tokenAddress = token.address;
          let decimals = token.decimals ?? 18;
          if (!tokenAddress) continue;
          if (normSym(token.symbol) === "WUSDT") {
            tokenAddress = WUSDT_ADDRESS;
            decimals = 6;
          } else if (
            normSym(token.symbol) === "USDT0" ||
            normSym(token.symbol) === "USDT"
          ) {
            tokenAddress = USDT_ADDRESS;
            decimals = 6;
          }
          const contract = new ethers.Contract(
            tokenAddress,
            ERC20ABI,
            provider
          );
          const raw = await contract.balanceOf(wallet);
          const balanceNum = Number(ethers.formatUnits(raw, decimals));
          balances[tokenAddress] = balanceNum;
        } catch (err) {
          console.error(`Error fetching balance for ${token.symbol}:`, err);
        }
      }
      setTokenBalances(balances);
    } catch (err) {
      console.log("RPC error:", err);
    }
  }, [wallet, selectedChain, importedTokens, isGold]);

  useEffect(() => {
    if (wallet && selectedChain) {
      fetchTokenBalances();
      const interval = setInterval(fetchTokenBalances, 10000);
      return () => clearInterval(interval);
    }
  }, [wallet, selectedChain, fetchTokenBalances]);

  // Native balance
  const getAccountTokens = useCallback(async () => {
    try {
      setFetching(true);
      const chain = CHAINS_CONFIG[selectedChain];
      if (!chain || !chain.rpcUrl) {
        setFetching(false);
        return;
      }
      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
      const bal = await provider.getBalance(wallet);
      setBalance(Number(ethers.formatEther(bal)));
      setFetching(false);
    } catch {
      setFetching(false);
    }
  }, [wallet, selectedChain]);

  useEffect(() => {
    if (wallet && selectedChain) {
      getAccountTokens();
      fetchTokenBalances();
    }
  }, [wallet, selectedChain, getAccountTokens, fetchTokenBalances]);

  // Send native tx
  async function sendTransaction(to, amount) {
    try {
      const chain = CHAINS_CONFIG[selectedChain];
      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
      const pk = ethers.Wallet.fromPhrase(seedPhrase).privateKey;
      const signer = new ethers.Wallet(pk, provider);
      const tx = await signer.sendTransaction({
        to,
        value: ethers.parseEther(amount),
      });
      setProcessing(true);
      setHash(tx.hash);
      await tx.wait();
      setProcessing(false);
      setHash(null);
      setAmountToSend("");
      setSendToAddress("");
      getAccountTokens();
      fetchTokenBalances();
    } catch {
      setProcessing(false);
      setHash(null);
    }
  }

  // Get balance for a token
  const getTokenBalance = (token) => {
    if (token.isBait || token.isNative) {
      return balance;
    }
    if (normSym(token.symbol) === "WUSDT") {
      return tokenBalances[WUSDT_ADDRESS] ?? 0;
    }
    if (normSym(token.symbol) === "USDT0" || normSym(token.symbol) === "USDT") {
      return tokenBalances[USDT_ADDRESS] ?? 0;
    }
    return tokenBalances[token.address] ?? 0;
  };

  // ============================================================
  // TOKEN LIST — strict de-duplication
  // ============================================================
  const tokenList = useMemo(() => {
    const nativeBaseToken = isGold
      ? {
          symbol: "BAIT",
          name: "Gold Backed Token",
          isBait: true,
          isNative: true,
          logo: BaitLogo,
          price: baitPrice,
          change: "+2.17%",
        }
      : {
          symbol: nativeSymbol,
          name: nativeName,
          isNative: true,
          logo: nativeLogo,
          change: "+2.17%",
        };

    const baseTokens = [nativeBaseToken];

    const seenAddresses = new Set();
    const seenSymbols = new Set([normSym(nativeSymbol)]);

    if (isGold) {
      const wusdtLower = normAddr(WUSDT_ADDRESS);
      const wusdtRemoved = removedTokens.includes(wusdtLower);

      const hasWUSDTImported = importedTokens.some((t) => {
        const addr = normAddr(t.address);
        const sym = normSym(t.symbol);
        return addr === wusdtLower || sym === "WUSDT";
      });

      if (!hasWUSDTImported && !wusdtRemoved) {
        baseTokens.push({
          symbol: "WUSDT",
          name: "Wrapped USDT",
          address: WUSDT_ADDRESS,
          decimals: 6,
          logo: TETHER_LOGO_URL,
          change: "+0.45%",
        });
        seenAddresses.add(wusdtLower);
        seenSymbols.add("WUSDT");
      }
    }

    const safeImported = [];
    for (const t of importedTokens) {
      const addr = normAddr(t.address);
      const sym = normSym(t.symbol);

      if (removedTokens.includes(addr)) continue;
      if (addr && seenAddresses.has(addr)) continue;
      if (sym && seenSymbols.has(sym)) continue;

      if (addr) seenAddresses.add(addr);
      if (sym) seenSymbols.add(sym);

      const resolvedLogo = isStablecoinSymbol(sym)
        ? TETHER_LOGO_URL
        : tokenLogos[t.address] || null;

      safeImported.push({
        ...t,
        logo: resolvedLogo,
        change:
          Math.random() > 0.5
            ? `+${(Math.random() * 2).toFixed(2)}%`
            : `-${(Math.random() * 2).toFixed(2)}%`,
      });
    }

    return [...baseTokens, ...safeImported];
  }, [
    importedTokens,
    tokenLogos,
    selectedChain,
    baitPrice,
    nativeSymbol,
    nativeName,
    nativeLogo,
    isGold,
    removedTokens,
  ]);

  const filteredTokens = useMemo(() => {
    if (!searchQuery) return tokenList;
    const query = searchQuery.toLowerCase();
    return tokenList.filter(
      (token) =>
        token.symbol.toLowerCase().includes(query) ||
        token.name?.toLowerCase().includes(query)
    );
  }, [tokenList, searchQuery]);

  if (selectedToken) {
    return (
      <TokenDetails
        token={selectedToken}
        wallet={wallet}
        seedPhrase={seedPhrase}
        selectedChain={selectedChain}
        balance={getTokenBalance(selectedToken)}
        onBack={() => setSelectedToken(null)}
      />
    );
  }

  const renderTokenList = () => (
    <>
      <div className="tokens-header">
        <div className="tokens-tabs">
          <span
            className={`token-tab ${activeTab === "tokens" ? "active" : ""}`}
            onClick={() => setActiveTab("tokens")}
          >
            Tokens
          </span>
          <span
            className={`token-tab ${activeTab === "activity" ? "active" : ""}`}
            onClick={() => setActiveTab("activity")}
          >
            Activity
          </span>
        </div>
        <ReloadOutlined
          onClick={() => {
            if (activeTab === "activity") {
              fetchTransactions();
            } else {
              fetchTokenBalances();
              fetchAllTokenLogos();
              loadImportedTokens();
            }
          }}
          className="reload-icon"
        />
      </div>

      {activeTab === "activity" ? (
        renderActivityTab()
      ) : (
        <>
          <div className="token-search">
            <SearchOutlined className="search-icon" />
            <input
              type="text"
              placeholder="Search token or contract address..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="token-list-container">
            <List
              dataSource={filteredTokens}
              renderItem={(item) => {
                if (item.isBait || item.isNative) {
                  const usdValue = balance * (baitPrice || 0);
                  const isBaitToken = !!item.isBait;
                  const tokenLogo = item.logo || BaitLogo;

                  return (
                    <List.Item
                      className="modern-token-card"
                      style={{ cursor: "default" }}
                    >
                      <div className="token-left">
                        <div className="token-avatar">
                          <img
                            src={tokenLogo}
                            alt={item.symbol}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = BaitLogo;
                            }}
                          />
                        </div>
                        <div className="token-text">
                          <div className="token-symbol-row">
                            <span className="token-symbol">{item.symbol}</span>
                            {isBaitToken && (
                              <span className="token-badge gold-backed">
                                Gold Backed
                              </span>
                            )}
                          </div>
                          <div className="token-name">{item.name}</div>
                        </div>
                      </div>
                      <div className="token-right">
                        <div className="token-balance">
                          {balance.toFixed(4)}
                        </div>
                        <div className="token-sub">${usdValue.toFixed(2)}</div>
                        <div className="token-change positive">
                          {item.change || "+2.17%"}
                        </div>
                      </div>
                    </List.Item>
                  );
                }

                const bal = getTokenBalance(item) || 0;
                const usdValue = bal * (baitPrice || 1);
                const isWUSDT = normSym(item.symbol) === "WUSDT";
                const isStable = isStablecoinSymbol(item.symbol);

                const resolvedLogoSrc = isStable
                  ? TETHER_LOGO_URL
                  : getTokenLogoUrl({
                      ...item,
                      logoUrl: item.logo,
                      chainName: storageKey,
                    });

                return (
                  <List.Item
                    className="modern-token-card"
                    style={{ cursor: "default" }}
                  >
                    <div className="token-left">
                      <div className="token-avatar">
                        <img
                          src={resolvedLogoSrc}
                          alt={item.symbol}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = isStable
                              ? TETHER_LOGO_URL
                              : getFallbackLogo(item.symbol);
                          }}
                        />
                      </div>
                      <div className="token-text">
                        <div className="token-symbol-row">
                          <span className="token-symbol">{item.symbol}</span>
                          {(isWUSDT || isStable) && (
                            <span className="token-badge gold-backed">
                              {isWUSDT ? "Gold Backed" : "Stablecoin"}
                            </span>
                          )}
                        </div>
                        <div className="token-name">{item.name}</div>
                      </div>
                    </div>
                    <div className="token-right">
                      <div className="token-balance">{bal.toFixed(6)}</div>
                      <div className="token-sub">${usdValue.toFixed(2)}</div>
                      <div
                        className={`token-change ${
                          item.change?.startsWith("+")
                            ? "positive"
                            : item.change?.startsWith("-")
                            ? "negative"
                            : ""
                        }`}
                      >
                        {item.change || "0.00%"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="token-delete-btn"
                      title={`Remove ${item.symbol}`}
                      aria-label={`Remove ${item.symbol}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteToken(item);
                      }}
                    >
                      <DeleteOutlined />
                    </button>
                  </List.Item>
                );
              }}
            />
            <ImportToken
              address={wallet}
              selectedChain={selectedChain}
              onImported={handleUserImported}
            />
          </div>
        </>
      )}
    </>
  );

  const renderModeToggle = () => (
    <div className="mode-toggle-wrapper">
      <Segmented
        className="app-mode-toggle premium-toggle"
        options={[
          { label: "BAITPAY", value: "BAITPAY" },
          { label: "BAITCASH", value: "BAITCASH" },
        ]}
        value={appMode}
        onChange={(value) => {
          setAppMode(value);
          if (value === "BAITCASH") {
            navigate("/dashboard");
          } else {
            navigate("/yourwallet");
          }
        }}
        size="small"
      />
    </div>
  );

  const renderSwapPage = () => (
    <div className="app-page">
      {renderModeToggle()}
      <div className="swap-page-title">
        <button
          type="button"
          className="market-back-btn"
          onClick={() => navigate("/yourwallet")}
          title="Back to Wallet"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="swap-title-content">
          <h2>SWAP</h2>
          <p>Swap tokens instantly on GoldChain.</p>
        </div>
      </div>
      <SwapInWallet
        wallet={wallet}
        seedPhrase={seedPhrase}
        selectedChain={selectedChain}
        balance={balance}
      />
    </div>
  );

  const renderBridgePage = () => (
    <div className="app-page">
      {renderModeToggle()}
      <div className="bridge-page-title">
        <button
          className="bridge-back-btn"
          onClick={() => navigate("/yourwallet")}
        >
          <ArrowLeftOutlined />
        </button>
        <div className="bridge-title-content">
          <h2>BRIDGE</h2>
          <p>Transfer assets securely between supported networks.</p>
        </div>
      </div>
      <BridgeWidget wallet={wallet} seedPhrase={seedPhrase} />
    </div>
  );

  const renderTransferPage = () => (
    <div className="app-page">
      {renderModeToggle()}
      <div className="send-page-title">
        <button
          className="send-back-btn"
          onClick={() => navigate("/yourwallet")}
        >
          <ArrowLeftOutlined />
        </button>
       <div>
  <h1
    style={{
      margin: 0,
      fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
      fontSize: "26px",
      fontWeight: 600,
      letterSpacing: "1.4px",
      lineHeight: 1.1,
      color: "#8a5a1f",
      WebkitTextStroke: "0.4px #6b3d0a",
      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    }}
  >
    Send
  </h1>
  <p
    style={{
      margin: "4px 0 0",
      fontFamily: "'Poppins', 'Segoe UI', sans-serif",
      fontSize: "11px",
      fontWeight: 500,
      letterSpacing: "0.6px",
      lineHeight: 1.3,
      color: "#a07a3f",
      opacity: 0.95,
    }}
  >
    Send {nativeSymbol} or supported assets
  </p>
</div>
      </div>
      <div className="app-transfer-page">
        <div className="app-transfer-balance">
          <div className="balance-left">
            <div className="app-transfer-label">Native Balance</div>
            <div className="app-transfer-amount">
              {balance.toFixed(6)}
              <span className="balance-ticker">{nativeSymbol}</span>
            </div>
            <div className="balance-usd">
              ≈ ${(balance * (baitPrice || 0)).toFixed(2)} USD
            </div>
          </div>
          <div className="balance-right">
            <img
              src={nativeLogo}
              alt={nativeSymbol}
              className="balance-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = BaitLogo;
              }}
            />
          </div>
        </div>

        <div className="send-form-wrapper">
          <div className="send-field">
            <div className="send-field-label">To</div>
            <Input
              value={sendToAddress}
              onChange={(e) => setSendToAddress(e.target.value)}
              placeholder="Enter wallet address"
              className="send-input"
            />
          </div>

          <div className="send-field">
            <div className="send-field-label">Asset</div>
            <div
              className="asset-selector"
              onClick={() => setShowAssetDropdown(!showAssetDropdown)}
            >
              <div className="asset-info">
                <img
                  src={selectedAsset?.logo || nativeLogo}
                  alt={selectedAsset?.symbol || nativeSymbol}
                  className="asset-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = BaitLogo;
                  }}
                />
                <div className="asset-details">
                  <span className="asset-symbol">
                    {selectedAsset?.symbol || nativeSymbol}
                  </span>
                  <span className="asset-name">
                    {selectedAsset?.name || nativeName}
                  </span>
                </div>
              </div>
              <div
                className={`asset-chevron ${showAssetDropdown ? "open" : ""}`}
              >
                ›
              </div>
            </div>

            {showAssetDropdown && (
              <div className="asset-dropdown">
                {assetOptions.map((asset) => (
                  <div
                    key={asset.symbol}
                    className={`asset-option ${
                      selectedAsset?.symbol === asset.symbol ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedAsset(asset);
                      setShowAssetDropdown(false);
                    }}
                  >
                    <img
                      src={asset.logo || BaitLogo}
                      alt={asset.symbol}
                      className="asset-option-logo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = BaitLogo;
                      }}
                    />
                    <div className="asset-option-details">
                      <span className="asset-option-symbol">
                        {asset.symbol}
                      </span>
                      <span className="asset-option-name">{asset.name}</span>
                    </div>
                    {selectedAsset?.symbol === asset.symbol && (
                      <span className="asset-option-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="send-field">
            <div className="send-field-label">Amount</div>
            <div className="amount-input-wrapper">
              <Input
                value={amountToSend}
                onChange={(e) => setAmountToSend(e.target.value)}
                placeholder="0.0"
                type="number"
                className="amount-input"
              />
              <button
                className="max-btn"
                onClick={() => setAmountToSend(balance.toString())}
              >
                MAX
              </button>
            </div>
            <div className="available-balance">
              Available: {balance.toFixed(6)}{" "}
              {selectedAsset?.symbol || nativeSymbol}
            </div>
          </div>
        </div>

        <div className="send-button-section">
          <div className="send-preview">
            <div className="send-preview-label">You will send</div>
            <div className="send-preview-amount">
              {amountToSend || "0.0"} {selectedAsset?.symbol || nativeSymbol}
            </div>
            <div className="send-preview-usd">
              ≈ $
              {((parseFloat(amountToSend) || 0) * (baitPrice || 0)).toFixed(2)}{" "}
              USD
            </div>
            <div className="send-preview-fee">
              <span className="fee-label">Network Fee</span>
              <span className="fee-value">
                - 0.00021 {selectedAsset?.symbol || nativeSymbol} ($0.001)
              </span>
            </div>
          </div>

          <button
            className="primaryBtn"
            onClick={() => sendTransaction(sendToAddress, amountToSend)}
            disabled={
              !sendToAddress ||
              !amountToSend ||
              parseFloat(amountToSend) <= 0 ||
              processing
            }
          >
            <img
              src={selectedAsset?.logo || nativeLogo}
              alt="Send"
              className="send-btn-icon"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = BaitLogo;
              }}
            />
            Send {selectedAsset?.symbol || nativeSymbol}
          </button>

          {processing && (
            <div className="send-processing">
              <Spin />
              {hash && (
                <Tooltip title={hash}>
                  <p className="send-hash">Hover For Tx Hash</p>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderMainPage = () => (
    <>
      {renderModeToggle()}
      <div className="wallet-page-title">
        <div>
          <h1>Web3 Wallet</h1>
          <p>Manage your assets and view balances</p>
        </div>
      </div>

      <div className="balance-card">
        <div className="balance-background-layer">
          <WalletBackground
            particleCount={20}
            particleSize={300}
            opacity={0.6}
            rotationSpeed={0.004}
            movementSpeed={0.04}
            color={0x85b2df}
            transparent={true}
            zIndex={0}
          />
        </div>
        <div className="balance-card-content">
          <div className="balance-card-top">
            <div className="balance-card-left">
              <div className="balance-label">Total Balance</div>
              <div className="balance-amount">
                $
                {(balance * (baitPrice || 0)).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="balance-bait">
                ≈ {balance.toFixed(4)} {nativeSymbol}
              </div>
            </div>
          </div>
          <div className="balance-address">
            <span className="address-text">{formatAddress(wallet)}</span>
            <button
              type="button"
              className={`copy-btn ${copied ? "copy-btn-copied" : ""}`}
              onClick={copyAddress}
              title={copied ? "Copied" : "Copy wallet address"}
              aria-label={
                copied ? "Wallet address copied" : "Copy wallet address"
              }
            >
              {copied ? <CheckOutlined /> : <CopyOutlined />}
            </button>
          </div>
          <div className="balance-actions">
            <button
              className="balance-action-btn"
              onClick={() => navigate("/receive")}
            >
              <div className="balance-action-icon receive-icon">
                <ArrowDownOutlined />
              </div>
              <span>Receive</span>
            </button>
            <button
              className="balance-action-btn"
              onClick={() => navigate("/send")}
            >
              <div className="balance-action-icon send-icon">
                <ArrowUpOutlined />
              </div>
              <span>Send</span>
            </button>
            <button
              className="balance-action-btn"
              onClick={() => navigate("/swap")}
            >
              <div className="balance-action-icon swap-icon">
                <SwapOutlined />
              </div>
              <span>Swap</span>
            </button>
            <button
              className="balance-action-btn"
              onClick={() => navigate("/bridge")}
            >
              <div className="balance-action-icon bridge-icon">
                <ApiOutlined />
              </div>
              <span>Bridge</span>
            </button>
          </div>
        </div>
      </div>

      <div className="tokens-section">
        <div className="tokens-glass-card">
          {fetching ? <Spin /> : renderTokenList()}
        </div>
      </div>
    </>
  );

  if (appMode === "BAITCASH" || location.pathname === "/dashboard") {
    return (
      <Dashboard wallet={wallet} seedPhrase={seedPhrase} logout={logout} />
    );
  }

  return (
    <div className="bait-dashboard">
      <div className="dashboard-main">
        {walletPage === "main" && renderMainPage()}
        {walletPage === "swap" && renderSwapPage()}
        {walletPage === "bridge" && renderBridgePage()}
        {walletPage === "transfer" && renderTransferPage()}
      </div>
    </div>
  );
}

export default WalletView;